import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ButtonLink } from "../components/ui";
import { ProtectedRoute } from "../features/auth/ProtectedRoute";
import { contentManagerRoles } from "../lib/content-permissions";
import { brand } from "../config/brand";
import { AppLayout } from "../layouts/AppLayout";
import { PublicLayout } from "../layouts/PublicLayout";
import { BrandedLoadingScreen } from "../components/BrandedLoadingScreen";
import { PublicSectionGate } from "../features/access/PublicSectionGate";
import { AuthenticatedModuleGate } from "../features/access/AuthenticatedModuleGate";
import { PortalAnalytics } from "../features/executive/PortalAnalytics";
import { useI18n } from "../features/i18n/I18nProvider";
import { VisualIdentityController } from "../features/content/VisualIdentityController";

const HomePage = lazy(() =>
  import("../pages/public/HomePage").then((module) => ({
    default: module.HomePage,
  })),
);
const InstitutionPage = lazy(() =>
  import("../pages/public/InstitutionPage").then((module) => ({
    default: module.InstitutionPage,
  })),
);
const SearchPage = lazy(() =>
  import("../pages/public/SearchPage").then((module) => ({
    default: module.SearchPage,
  })),
);
const proceduresPages = () => import("../pages/public/ProceduresPages");
const ProceduresPage = lazy(() =>
  proceduresPages().then((module) => ({ default: module.ProceduresPage })),
);
const ProcedureDetailPage = lazy(() =>
  proceduresPages().then((module) => ({ default: module.ProcedureDetailPage })),
);
const contentPages = () => import("../pages/public/ContentPages");
const TransparencyPage = lazy(() =>
  contentPages().then((module) => ({ default: module.TransparencyPage })),
);
const WorksPage = lazy(() =>
  contentPages().then((module) => ({ default: module.WorksPage })),
);
const TourismCulturePage = lazy(() =>
  contentPages().then((module) => ({ default: module.TourismCulturePage })),
);
const NewsPage = lazy(() =>
  contentPages().then((module) => ({ default: module.NewsPage })),
);
const NewsDetailPage = lazy(() =>
  contentPages().then((module) => ({ default: module.NewsDetailPage })),
);
const ContactPage = lazy(() =>
  contentPages().then((module) => ({ default: module.ContactPage })),
);
const AccessibilityPage = lazy(() =>
  contentPages().then((module) => ({ default: module.AccessibilityPage })),
);
const ServiceStatusPage = lazy(() =>
  contentPages().then((module) => ({ default: module.ServiceStatusPage })),
);
const ProjectInfoPage = lazy(() =>
  contentPages().then((module) => ({ default: module.ProjectInfoPage })),
);
const authPages = () => import("../pages/auth/AuthPages");
const LoginPage = lazy(() =>
  authPages().then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  authPages().then((module) => ({ default: module.RegisterPage })),
);
const RecoverPage = lazy(() =>
  authPages().then((module) => ({ default: module.RecoverPage })),
);
const citizenPages = () => import("../pages/citizen/CitizenPages");
const CitizenDashboardPage = lazy(() =>
  citizenPages().then((module) => ({ default: module.CitizenDashboardPage })),
);
const CitizenCasesPage = lazy(() =>
  citizenPages().then((module) => ({ default: module.CitizenCasesPage })),
);
const NewCasePage = lazy(() =>
  citizenPages().then((module) => ({ default: module.NewCasePage })),
);
const CitizenCaseDetailPage = lazy(() =>
  citizenPages().then((module) => ({ default: module.CitizenCaseDetailPage })),
);
const NotificationsPage = lazy(() =>
  citizenPages().then((module) => ({ default: module.NotificationsPage })),
);
const ContentAdminPage = lazy(() =>
  import("../features/content/ContentAdminPage").then((module) => ({
    default: module.ContentAdminPage,
  })),
);
const AccessGovernancePage = lazy(() =>
  import("../features/access/AccessGovernancePage").then((module) => ({
    default: module.AccessGovernancePage,
  })),
);
const AuditPage = lazy(() =>
  import("../features/audit/AuditPage").then((module) => ({
    default: module.AuditPage,
  })),
);
const managementPages = () => import("../pages/management/ManagementPages");
const ManagementDashboardPage = lazy(() =>
  managementPages().then((module) => ({
    default: module.ManagementDashboardPage,
  })),
);
const IntakeInboxPage = lazy(() =>
  managementPages().then((module) => ({ default: module.IntakeInboxPage })),
);
const ManagementCaseDetailPage = lazy(() =>
  managementPages().then((module) => ({
    default: module.ManagementCaseDetailPage,
  })),
);
const ManagementPlaceholderPage = lazy(() =>
  managementPages().then((module) => ({
    default: module.ManagementPlaceholderPage,
  })),
);
const mobilityPages = () => import("../pages/mobility/MobilityPages");
const MobilityLandingPage = lazy(() =>
  mobilityPages().then((module) => ({ default: module.MobilityLandingPage })),
);
const TransitTrackingPage = lazy(() =>
  mobilityPages().then((module) => ({ default: module.TransitTrackingPage })),
);
const CollectionTrackingPage = lazy(() =>
  mobilityPages().then((module) => ({ default: module.CollectionTrackingPage })),
);
const MachineryTrackingPage = lazy(() =>
  mobilityPages().then((module) => ({ default: module.MachineryTrackingPage })),
);
const MobilityAdminPage = lazy(() =>
  mobilityPages().then((module) => ({ default: module.MobilityAdminPage })),
);
const CadastralPage = lazy(() =>
  import("../pages/cadastre/CadastralPage").then((module) => ({
    default: module.CadastralPage,
  })),
);
const academyPages = () => import("../pages/academy/AcademyPages");
const AcademyLandingPage = lazy(() =>
  academyPages().then((module) => ({ default: module.AcademyLandingPage })),
);
const AcademyLearningPage = lazy(() =>
  academyPages().then((module) => ({ default: module.AcademyLearningPage })),
);
const AcademyAdminPage = lazy(() =>
  academyPages().then((module) => ({ default: module.AcademyAdminPage })),
);
const paymentPages = () => import("../pages/payments/PaymentPages");
const CitizenPaymentsPage = lazy(() =>
  paymentPages().then((module) => ({ default: module.CitizenPaymentsPage })),
);
const TreasuryPaymentsPage = lazy(() =>
  paymentPages().then((module) => ({ default: module.TreasuryPaymentsPage })),
);

const pageTitles: Record<string, string> = {
  "/": "Inicio",
  "/buscar": "Buscar",
  "/tramites": "Trámites",
  "/intendencia": "Intendencia Municipal",
  "/junta-municipal": "Junta Municipal",
  "/dependencias": "Dependencias municipales",
  "/transparencia": "Transparencia",
  "/obras": "Obras",
  "/turismo-cultura": "Cultura y turismo",
  "/noticias": "Noticias",
  "/contacto": "Contacto",
  "/accesibilidad": "Accesibilidad",
  "/privacidad": "Privacidad",
  "/terminos": "Términos de uso",
  "/informacion-proyecto": "Información del proyecto",
  "/estado-servicios": "Estado de servicios",
  "/movilidad": "Movilidad y servicios",
  "/mapa-catastral": "Mapa Catastral",
  "/academia": brand.academyName,
  "/academia/mi-aprendizaje": "Mi aprendizaje",
  "/academia/gestion": "Gestión académica",
  "/movilidad/colectivos": "Transporte público",
  "/servicios/maquinarias": "Maquinarias municipales",
  "/servicios/recoleccion": "¿Dónde está el recolector?",
  "/gestion/movilidad": "Administración de movilidad",
  "/ingresar": "Ingresar",
  "/registro": "Registro",
  "/recuperar": "Recuperar acceso",
  "/mi": "Mi espacio",
  "/mi/tramites": "Mis trámites",
  "/mi/tramites/nuevo": "Nueva solicitud",
  "/mi/pagos": "Mis pagos",
  "/gestion": "Gestión",
  "/gestion/mesa-entrada": "Mesa de Entrada",
  "/gestion/pagos": "Pagos municipales",
  "/gestion/organizacion": "Organización y accesos",
  "/gestion/seguridad": "Auditoría municipal",
};

function RouteMetadata() {
  const location = useLocation();
  const { locale, t } = useI18n();
  useEffect(() => {
    const exact = pageTitles[location.pathname];
    const inferred = location.pathname.startsWith("/tramites/")
      ? "Ficha de trámite"
      : location.pathname.startsWith("/mi/tramites/")
        ? "Expediente"
        : location.pathname.startsWith("/gestion/expedientes/")
          ? "Gestión de expediente"
          : location.pathname.startsWith("/noticias/")
            ? "Noticia"
            : brand.name;
    document.title = `${t(exact ?? inferred)} — ${t(brand.name)}`;
  }, [locale, location.pathname, t]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    root.style.scrollBehavior = "auto";

    const resetScroll = () => {
      window.scrollTo(0, 0);
      root.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const positionRoute = () => {
      if (location.hash) {
        const target = document.getElementById(
          decodeURIComponent(location.hash.slice(1)),
        );
        if (target) {
          target.scrollIntoView({ block: "start" });
          return;
        }
      }
      resetScroll();
    };

    positionRoute();
    const frame = window.requestAnimationFrame(() => {
      positionRoute();
      root.style.scrollBehavior = previousScrollBehavior;
    });

    return () => {
      window.cancelAnimationFrame(frame);
      root.style.scrollBehavior = previousScrollBehavior;
    };
  }, [location.hash, location.pathname, location.search]);

  return null;
}

export function App() {
  return (
    <>
      <VisualIdentityController />
      <RouteMetadata />
      <PortalAnalytics />
      <Suspense fallback={<BrandedLoadingScreen />}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route
              index
              element={
                <PublicSectionGate moduleCode="public.home">
                  <HomePage />
                </PublicSectionGate>
              }
            />
            <Route path="buscar" element={<SearchPage />} />
            <Route path="institucion" element={<Navigate to="/intendencia" replace />} />
            <Route
              path="intendencia"
              element={
                <PublicSectionGate moduleCode="public.institution">
                  <InstitutionPage section="intendance" />
                </PublicSectionGate>
              }
            />
            <Route
              path="junta-municipal"
              element={
                <PublicSectionGate moduleCode="public.institution">
                  <InstitutionPage section="board" />
                </PublicSectionGate>
              }
            />
            <Route
              path="dependencias"
              element={
                <PublicSectionGate moduleCode="public.institution">
                  <InstitutionPage section="departments" />
                </PublicSectionGate>
              }
            />
            <Route
              path="tramites"
              element={
                <PublicSectionGate moduleCode="public.procedures">
                  <ProceduresPage />
                </PublicSectionGate>
              }
            />
            <Route
              path="tramites/:slug"
              element={
                <PublicSectionGate moduleCode="public.procedures">
                  <ProcedureDetailPage />
                </PublicSectionGate>
              }
            />
            <Route
              path="transparencia"
              element={
                <PublicSectionGate moduleCode="public.transparency">
                  <TransparencyPage />
                </PublicSectionGate>
              }
            />
            <Route
              path="obras"
              element={
                <PublicSectionGate moduleCode="public.works">
                  <WorksPage />
                </PublicSectionGate>
              }
            />
            <Route
              path="turismo-cultura"
              element={
                <PublicSectionGate moduleCode="public.tourism">
                  <TourismCulturePage />
                </PublicSectionGate>
              }
            />
            <Route
              path="noticias"
              element={
                <PublicSectionGate moduleCode="public.news">
                  <NewsPage />
                </PublicSectionGate>
              }
            />
            <Route
              path="noticias/:slug"
              element={
                <PublicSectionGate moduleCode="public.news">
                  <NewsDetailPage />
                </PublicSectionGate>
              }
            />
            <Route
              path="contacto"
              element={
                <PublicSectionGate moduleCode="public.contact">
                  <ContactPage />
                </PublicSectionGate>
              }
            />
            <Route path="accesibilidad" element={<AccessibilityPage />} />
            <Route path="estado-servicios" element={<ServiceStatusPage />} />
            <Route path="informacion-proyecto" element={<ProjectInfoPage />} />
            <Route
              path="movilidad"
              element={
                <PublicSectionGate moduleCode="public.mobility">
                  <MobilityLandingPage />
                </PublicSectionGate>
              }
            />
            <Route path="privacidad" element={<ProjectInfoPage kind="privacy" />} />
            <Route path="terminos" element={<ProjectInfoPage kind="terms" />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          <Route
            path="/movilidad/colectivos"
            element={
              <PublicSectionGate moduleCode="public.mobility">
                <TransitTrackingPage />
              </PublicSectionGate>
            }
          />
          <Route
            path="/servicios/maquinarias"
            element={
              <PublicSectionGate moduleCode="public.mobility">
                <MachineryTrackingPage />
              </PublicSectionGate>
            }
          />
          <Route
            path="/servicios/recoleccion"
            element={
              <PublicSectionGate moduleCode="public.mobility">
                <CollectionTrackingPage />
              </PublicSectionGate>
            }
          />
          <Route
            path="/mapa-catastral"
            element={
              <PublicSectionGate moduleCode="public.cadastre">
                <CadastralPage />
              </PublicSectionGate>
            }
          />

          <Route path="/ingresar" element={<LoginPage />} />
          <Route path="/registro" element={<RegisterPage />} />
          <Route path="/recuperar" element={<RecoverPage />} />

          <Route element={<ProtectedRoute roles={["student"]} />}>
            <Route element={<AppLayout />}>
              <Route
                path="/academia"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.academy">
                    <AcademyLandingPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/academia/mi-aprendizaje"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.academy">
                    <AcademyLearningPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route
            element={
              <ProtectedRoute
                roles={[
                  "super_admin",
                  "municipal_admin",
                  "system_admin",
                  "academic_admin",
                  "academic_editor",
                ]}
              />
            }
          >
            <Route element={<AppLayout management />}>
              <Route
                path="/academia/gestion"
                element={
                  <AuthenticatedModuleGate moduleCode="management.academy">
                    <AcademyAdminPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route element={<ProtectedRoute roles={["citizen"]} />}>
            <Route element={<AppLayout />}>
              <Route
                path="/mi"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.dashboard">
                    <CitizenDashboardPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/mi/tramites"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.cases">
                    <CitizenCasesPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/mi/tramites/nuevo"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.cases">
                    <NewCasePage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/mi/tramites/:id"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.cases">
                    <CitizenCaseDetailPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/mi/notificaciones"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.notifications">
                    <NotificationsPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/mi/pagos"
                element={
                  <AuthenticatedModuleGate moduleCode="citizen.payments">
                    <CitizenPaymentsPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route element={<ProtectedRoute roles={contentManagerRoles} />}>
            <Route element={<AppLayout management />}>
              <Route
                path="/gestion/contenidos"
                element={
                  <AuthenticatedModuleGate moduleCode="management.content">
                    <ContentAdminPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route
            element={
              <ProtectedRoute
                roles={[
                  "super_admin",
                  "municipal_admin",
                  "mayor",
                  "general_secretary",
                  "intake_agent",
                  "intake_supervisor",
                  "system_admin",
                  "fleet_operator",
                  "designer",
                  "news_editor",
                  "treasury_agent",
                  "auditor",
                  "security_officer",
                  "executive_viewer",
                ]}
              />
            }
          >
            <Route element={<AppLayout management />}>
              <Route
                path="/gestion"
                element={
                  <AuthenticatedModuleGate moduleCode="management.dashboard">
                    <ManagementDashboardPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/gestion/dependencias"
                element={
                  <AuthenticatedModuleGate moduleCode="management.organization">
                    <ManagementPlaceholderPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/gestion/organizacion"
                element={
                  <AuthenticatedModuleGate moduleCode="management.organization">
                    <AccessGovernancePage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/gestion/movilidad"
                element={
                  <AuthenticatedModuleGate moduleCode="management.mobility">
                    <MobilityAdminPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route
            element={
              <ProtectedRoute
                roles={[
                  "super_admin",
                  "municipal_admin",
                  "mayor",
                  "general_secretary",
                  "system_admin",
                  "auditor",
                  "security_officer",
                ]}
              />
            }
          >
            <Route element={<AppLayout management />}>
              <Route
                path="/gestion/seguridad"
                element={
                  <AuthenticatedModuleGate moduleCode="management.security">
                    <AuditPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route
            element={
              <ProtectedRoute
                roles={[
                  "super_admin",
                  "municipal_admin",
                  "mayor",
                  "general_secretary",
                  "treasury_agent",
                  "auditor",
                  "executive_viewer",
                ]}
              />
            }
          >
            <Route element={<AppLayout management />}>
              <Route
                path="/gestion/pagos"
                element={
                  <AuthenticatedModuleGate moduleCode="management.payments">
                    <TreasuryPaymentsPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route
            element={<ProtectedRoute roles={["intake_agent", "intake_supervisor"]} />}
          >
            <Route element={<AppLayout management />}>
              <Route
                path="/gestion/mesa-entrada"
                element={
                  <AuthenticatedModuleGate moduleCode="management.intake">
                    <IntakeInboxPage />
                  </AuthenticatedModuleGate>
                }
              />
              <Route
                path="/gestion/expedientes/:id"
                element={
                  <AuthenticatedModuleGate moduleCode="management.intake">
                    <ManagementCaseDetailPage />
                  </AuthenticatedModuleGate>
                }
              />
            </Route>
          </Route>

          <Route path="/portal" element={<Navigate to="/mi" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <section className="section container not-found-page">
      <div className="not-found-number" aria-hidden="true">404</div>
      <p className="eyebrow">Ruta no encontrada</p>
      <h1>¡Parece que te perdiste por Piribebuy!</h1>
      <p>La página que buscás no existe o cambió de ubicación.</p>
      <div className="not-found-actions">
        <ButtonLink to="/">Volver al inicio</ButtonLink>
        <button className="not-found-back" type="button" onClick={() => navigate(-1)}>
          Volver atrás
        </button>
      </div>
    </section>
  );
}


